// A channelization scheme is data: channels, band-plan segments, and point
// markers, all in Hz. Built-ins are generated from formulas or transcribed;
// user schemes arrive as JSON and are validated here.

import { parseFrequency, UnitError } from '../units.ts'

export interface Channel {
  label: string
  centerHz: number
  widthHz: number
  group?: string
}

export interface Band {
  label: string
  lowHz: number
  highHz: number
  group?: string
}

export interface Marker {
  label: string
  hz: number
}

export interface Scheme {
  /** Stable key used in settings; user schemes get one on import. */
  id: string
  name: string
  /** Where the numbers came from. */
  source?: string
  builtin?: boolean
  channels: Channel[]
  bands: Band[]
  markers: Marker[]
}

export class SchemeError extends Error {
  override name = 'SchemeError'
}

/** Sanity cap on user input; a scheme past this is not a scheme. */
const MAX_ITEMS = 20_000
const MAX_LABEL = 60

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

/** A frequency from JSON: Hz as a number, or "906.875M" as a string. */
function hz(value: unknown, where: string): number {
  if (typeof value === 'number') {
    if (!Number.isFinite(value) || value < 0) throw new SchemeError(`${where}: frequency must be a finite number of Hz`)
    return value
  }
  if (typeof value === 'string') {
    try {
      const parsed = parseFrequency(value)
      if (parsed < 0) throw new SchemeError(`${where}: frequency must not be negative`)
      return parsed
    } catch (error) {
      if (error instanceof UnitError) throw new SchemeError(`${where}: ${error.message}`)
      throw error
    }
  }
  throw new SchemeError(`${where}: frequency missing`)
}

function label(value: unknown, fallback: string, where: string): string {
  if (value === undefined || value === null) return fallback
  if (typeof value !== 'string') throw new SchemeError(`${where}: label must be a string`)
  const trimmed = value.trim()
  if (trimmed.length > MAX_LABEL) throw new SchemeError(`${where}: label longer than ${MAX_LABEL} characters`)
  return trimmed || fallback
}

function group(value: unknown, where: string): string | undefined {
  if (value === undefined || value === null || value === '') return undefined
  if (typeof value !== 'string') throw new SchemeError(`${where}: group must be a string`)
  return value.trim().slice(0, MAX_LABEL)
}

function list(value: unknown, where: string): unknown[] {
  if (value === undefined || value === null) return []
  if (!Array.isArray(value)) throw new SchemeError(`${where} must be an array`)
  if (value.length > MAX_ITEMS) throw new SchemeError(`${where}: more than ${MAX_ITEMS} entries`)
  return value
}

const mhz = (value: number) => `${(value / 1e6).toFixed(4).replace(/\.?0+$/, '')} MHz`

/**
 * Turns parsed JSON into a Scheme or throws a SchemeError naming the field.
 * Frequencies may be Hz numbers or unit strings; labels default to the
 * frequency; an empty scheme is rejected because it could never draw.
 */
export function validateScheme(input: unknown, id: string): Scheme {
  if (!isRecord(input)) throw new SchemeError('a scheme is a JSON object')
  const name = label(input.name, '', 'name')
  if (!name) throw new SchemeError('name is required')

  const channels: Channel[] = list(input.channels, 'channels').map((raw, i) => {
    const where = `channels[${i}]`
    if (!isRecord(raw)) throw new SchemeError(`${where} must be an object`)
    // `centreHz` is accepted too: schemes saved before the spelling change use it.
    const centerHz = hz(raw.centerHz ?? raw.centreHz, `${where}.centerHz`)
    const widthHz = hz(raw.widthHz, `${where}.widthHz`)
    if (!(widthHz > 0)) throw new SchemeError(`${where}.widthHz must be positive`)
    const g = group(raw.group, `${where}.group`)
    return { label: label(raw.label, mhz(centerHz), where), centerHz, widthHz, ...(g ? { group: g } : {}) }
  })

  const bands: Band[] = list(input.bands, 'bands').map((raw, i) => {
    const where = `bands[${i}]`
    if (!isRecord(raw)) throw new SchemeError(`${where} must be an object`)
    const lowHz = hz(raw.lowHz, `${where}.lowHz`)
    const highHz = hz(raw.highHz, `${where}.highHz`)
    if (!(highHz > lowHz)) throw new SchemeError(`${where}: highHz must be above lowHz`)
    const g = group(raw.group, `${where}.group`)
    return { label: label(raw.label, `${mhz(lowHz)}–${mhz(highHz)}`, where), lowHz, highHz, ...(g ? { group: g } : {}) }
  })

  const markers: Marker[] = list(input.markers, 'markers').map((raw, i) => {
    const where = `markers[${i}]`
    if (!isRecord(raw)) throw new SchemeError(`${where} must be an object`)
    const at = hz(raw.hz, `${where}.hz`)
    return { label: label(raw.label, mhz(at), where), hz: at }
  })

  if (channels.length + bands.length + markers.length === 0) {
    throw new SchemeError('a scheme needs at least one channel, band, or marker')
  }

  const source = typeof input.source === 'string' ? input.source.trim().slice(0, 200) : undefined
  return { id, name, ...(source ? { source } : {}), channels, bands, markers }
}

/** Lowest and highest frequency the scheme touches, or null when empty. */
export function schemeExtent(scheme: Scheme): { lowHz: number; highHz: number } | null {
  let low = Infinity
  let high = -Infinity
  for (const c of scheme.channels) {
    low = Math.min(low, c.centerHz - c.widthHz / 2)
    high = Math.max(high, c.centerHz + c.widthHz / 2)
  }
  for (const b of scheme.bands) {
    low = Math.min(low, b.lowHz)
    high = Math.max(high, b.highHz)
  }
  for (const m of scheme.markers) {
    low = Math.min(low, m.hz)
    high = Math.max(high, m.hz)
  }
  return low <= high ? { lowHz: low, highHz: high } : null
}

// ------------------------------------------------------------ user storage

const USER_KEY = 'spectrum-mapper.schemes.v1'

export function loadUserSchemes(): Scheme[] {
  let raw: string | null = null
  try {
    raw = localStorage.getItem(USER_KEY)
  } catch {
    return []
  }
  if (!raw) return []
  try {
    const parsed: unknown = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    const out: Scheme[] = []
    for (const entry of parsed) {
      // Re-validated on load so a hand-edited or older record cannot crash the
      // renderer; a bad one is dropped rather than taking the rest with it.
      try {
        const id = isRecord(entry) && typeof entry.id === 'string' ? entry.id : newUserSchemeId()
        out.push(validateScheme(entry, id))
      } catch {
        continue
      }
    }
    return out
  } catch {
    return []
  }
}

export function saveUserSchemes(schemes: readonly Scheme[]): void {
  try {
    localStorage.setItem(USER_KEY, JSON.stringify(schemes))
  } catch {
    // Storage unavailable; the scheme lives for the session only.
  }
}

export function newUserSchemeId(): string {
  return `user-${Date.now().toString(36)}-${Math.floor(Math.random() * 1e6).toString(36)}`
}

// ------------------------------------------------------------------ color

/** Hues that read on both the dark heat and the dark strip, in assignment order. */
const COLORS = ['#ff8a5c', '#5cc8ff', '#8cff9a', '#ffb84d', '#d99cff', '#ff6fcf', '#7ff2e0', '#f2ee7f']

export function schemeColor(index: number): string {
  return COLORS[index % COLORS.length]!
}
